import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {UIGameEndTabButtonsView} from './GameEndTabButtonsView'
import {UIGameEndTableView} from './GameEndTableView'
import {GameEndStatsOverviewView} from './GameEndStatsOverviewView'
import {GameEndStatsResourceStatsView} from './GameEndStatsResourceStatsView'
import {GameEndStatsActivityStatsView} from './GameEndStatsActivityStatsView'
import {UIGameEndPlayerStatsController} from './UIGameEndPlayerStatsController'
import {UIGameEndTableHeadView} from './UIGameEndTableHeadView'
import {UIGameManager} from '../UIGameManager'
import {GameEndTabButtonView} from '../../ui_canvas_engine/GameEndTabButtonView'
import {UIGameEndChartView} from './UIGameEndChartView'
import {GameEndStatsDiceStatsView} from './GameEndStatsDiceStatsView'
import {GameEndStatsResCardStatsView} from './GameEndStatsResCardStatsView'
import {GameEndStatsDevCardStatsView} from './GameEndStatsDevCardStatsView'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {HideableEndGameSection, UIGameEndController} from './UIGameEndController'
import {EndGameState} from '@colonist/game-states'
import {GameModeTranslationKeyContext} from '@colonist/i18n'

interface EndGameTab {
    button: GameEndTabButtonView
    container: UIGameEndChartView | UIGameEndTableView
}

export abstract class UIGameEndStatsController implements HideableEndGameSection {
    protected readonly uiGameManager: UIGameManager
    protected readonly uiGameEndController: UIGameEndController
    protected readonly endGameState: EndGameState

    readonly container: SectionBgView
    tabButtonsView: UIGameEndTabButtonsView
    protected tableHeadView: UIGameEndTableHeadView
    protected statsView: UIGameEndPlayerStatsController
    protected chartContainer: SectionBgView

    readonly itemSize: number
    readonly rowHeight: number
    readonly tableHeadHeight: number

    private overViewContainer: GameEndStatsOverviewView
    private resourceStatsContainer: UIGameEndTableView
    private activityStatsContainer: UIGameEndTableView

    private readonly tabs: EndGameTab[] = []
    protected activeTab: EndGameTab

    constructor(uiGameManager: UIGameManager, uiGameEndController: UIGameEndController, y: number, h: number, container: SectionBgView, endGameState: EndGameState) {
        this.uiGameManager = uiGameManager
        this.uiGameEndController = uiGameEndController
        this.endGameState = endGameState
        this.container = new SectionBgView(0, y, container.width, h, container)
        this.itemSize = this.container.height / 9

        const isUserOnMobileApp = UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI
        this.rowHeight = this.itemSize * (isUserOnMobileApp ? 1.7 : 1)
        this.tableHeadHeight = this.itemSize * (isUserOnMobileApp ? 1.2 : 1)

        this.buildTabs(this.uiGameManager.gameState.gameModeData.translationKeyContext)

        this.hideAll()
        this.activeTab = this.tabs[0]
        this.clickedTab(this.activeTab.button)
    }

    protected abstract buildTabs(gameModeTranslationKeyContext: GameModeTranslationKeyContext)

    protected createStatsView() {
        const h = this.itemSize * 7 + this.tableHeadHeight
        const y = this.itemSize
        this.statsView = new UIGameEndPlayerStatsController(this.uiGameManager, 0, y, this.container.width, h, this.container, this, this.endGameState)
        if(!UIUtilsEjsVariables.isUserOnMobileApp) return
        this.statsView.horizontalScrollBox.on('pointerdown', () => this.uiGameManager.toolTipController.closeTip())
        this.statsView.verticalScrollBox.on('pointerdown', () => this.uiGameManager.toolTipController.closeTip())
    }

    protected createTableTabs() {
        const tableContainer = this.statsView.horizontalScrollBox.content
        const w = tableContainer.width
        const h = tableContainer.height
        const columnWidth = this.itemSize

        this.tableHeadView = new UIGameEndTableHeadView(this.uiGameManager, 0, 0, w, this.tableHeadHeight, tableContainer, columnWidth, this, this.endGameState)

        this.overViewContainer = new GameEndStatsOverviewView(this.uiGameEndController, 0, this.tableHeadHeight, w, h, tableContainer, columnWidth, this.rowHeight, this.endGameState)
        this.resourceStatsContainer = new GameEndStatsResourceStatsView(0, this.tableHeadHeight, w, h, tableContainer, columnWidth, this.rowHeight, this.endGameState)
        this.activityStatsContainer = new GameEndStatsActivityStatsView(0, this.tableHeadHeight, w, h, tableContainer, columnWidth, this.rowHeight, this.endGameState)

        this.tabs.push({button: this.tabButtonsView.overView, container: this.overViewContainer})
        this.tabs.push({button: this.tabButtonsView.resourceStats, container: this.resourceStatsContainer})
        this.tabs.push({button: this.tabButtonsView.activityStats, container: this.activityStatsContainer})
    }

    protected createChartTabs() {
        const diceStatsContainer = new GameEndStatsDiceStatsView(0, 0, this.chartContainer.width, this.chartContainer.height,
            this.chartContainer, this.endGameState.diceStats)
        const resCardStatsContainer = new GameEndStatsResCardStatsView(0, 0, this.chartContainer.width, this.chartContainer.height,
            this.chartContainer, this.endGameState.resourceCardsStats,
            this.uiGameManager.gameState.gameModeData.activeResourceCardTypes)
        const devCardStatsContainer = new GameEndStatsDevCardStatsView(0, 0, this.chartContainer.width, this.chartContainer.height,
            this.chartContainer, this.endGameState.developmentCardStats,
            this.uiGameManager.gameState.gameModeData.activeDevelopmentCardTypes,
            this.uiGameManager.gameState.gameModeData.translationKeyContext)

        this.tabs.push({button: this.tabButtonsView.diceStats, container: diceStatsContainer})
        this.tabs.push({button: this.tabButtonsView.resourceCardStats, container: resCardStatsContainer})
        this.tabs.push({button: this.tabButtonsView.developmentCardStats, container: devCardStatsContainer})
    }

    hide() {
        this.container.visible = false
    }

    show() {
        this.container.visible = true
    }

    showActiveView() {
        this.activeTab.container.show()
    }

    hideActiveView() {
        this.activeTab.container.hide()
    }

    protected hideAll() {
        this.tabs.forEach((tab: EndGameTab) => {
            tab.container.hide()
            tab.button.removeHighlight()
        })
    }

    clickedTab(clickedTabButton: GameEndTabButtonView) {
        const clickedTab = this.tabs.find((tab: EndGameTab) => tab.button == clickedTabButton)
        if(!clickedTab) return

        this.activeTab.button.removeHighlight()
        this.hideActiveView()

        this.activeTab = clickedTab
        clickedTabButton.highlight()
        this.showActiveView()
    }
}
